import { useRltCalculationShowQuery } from "@/services/backend/rlt/calculations/api/calculation-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsHeader,
  DocumentSheetContent,
  DocumentSheetContextProvider,
  DocumentSheetHeader,
  DocumentSheetMain,
  DocumentSheetTitle,
  DocumentSheetTrigger,
  useDocumentSheetContext,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import t from "@/lang/lang";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { TreatmentComponent } from "@/services/backend/rlt/treatments/components/treatment-component";
import { Column } from "@/services/backend/rlt/treatments/lib/treatment-component-state";
import { CalculationComponent } from "@/routes/processes/[processId]/rlt/calculations/[calculationId]/calculation/calculation-route";
import { Button } from "@/shared/components/ui/button";
import { Calculation } from "@/services/backend/rlt/calculations/types/calculation";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { RefreshCw } from "lucide-react";
import { NavigateToDocumentButton } from "@/services/backend/documents/components/preview/_components/navigate-to-document-button";
import {
  DocumentPage,
  DocumentPageContent,
  DocumentPageHeader,
} from "@/shared/bsm/core/document/components/ui/document-page";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";

export function RltCalculationPreview({
  calculationId,
}: {
  calculationId: string;
}) {
  const {
    data: calculation,
    isLoading,
    error,
  } = useRltCalculationShowQuery({ id: calculationId });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return <RefreshCw className="h-4 w-4" />;
  }

  return (
    <DocumentSheetContextProvider>
      <CalculationDocumentSheet calculation={calculation!} />
    </DocumentSheetContextProvider>
  );
}

function CalculationDocumentSheet({
  calculation,
}: {
  calculation: Calculation;
}) {
  const { open, setOpen } = useDocumentSheetContext();

  return (
    <DocumentSheet open={open} onOpenChange={setOpen}>
      <DocumentSheetTrigger asChild>
        <Button variant="ghost" size="sm">
          {t("Vorschau")}
        </Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>
            {t(calculation!.documentKind)} {t("Vorschau")}
          </DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <CalculationPreview calculation={calculation!} />
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function CalculationPreview({ calculation }: { calculation: Calculation }) {
  return (
    <>
      <CalculationView calculation={calculation} />
      <CalculationsActions calculation={calculation} />
    </>
  );
}

function CalculationView({ calculation }: { calculation: Calculation }) {
  return (
    <div className="md:col-span-4">
      <PersistenceContextProvider>
        <DocumentPage>
          <Tabs defaultValue="treatment">
            <DocumentPageHeader>
              <TabsList>
                <TabsTrigger value="treatment">{t("Leistungen")}</TabsTrigger>
                <TabsTrigger value="calculation">
                  {t("Kalkulation")}
                </TabsTrigger>
              </TabsList>
              <PersistenceStateUsingContext className="mr-2 ml-auto" />
            </DocumentPageHeader>
            <DocumentPageContent className="max-h-[75vh] overflow-auto">
              <TabsContent value="treatment">
                <TreatmentComponent
                  treatmentId={calculation.treatmentId}
                  availableColumns={[
                    Column.Position,
                    Column.ComponentKind,
                    Column.ComponentData,
                    Column.Cycle,
                    Column.Formula,
                    Column.CleanTime,
                    Column.Comment,
                    Column.Actions,
                  ]}
                  defaultColumns={[
                    Column.Position,
                    Column.ComponentKind,
                    Column.ComponentData,
                    Column.Cycle,
                    Column.CleanTime,
                    Column.Comment,
                    Column.Actions,
                  ]}
                />
              </TabsContent>
              <TabsContent value="calculation">
                <CalculationComponent calculationId={calculation.id} />
              </TabsContent>
            </DocumentPageContent>
          </Tabs>
        </DocumentPage>
      </PersistenceContextProvider>
    </div>
  );
}

function CalculationsActions({ calculation }: { calculation: Calculation }) {
  return (
    <DocumentSheetActions className="md:col-span-1">
      <DocumentSheetActionsHeader
        title={t(calculation.documentKind)}
        documentNumber={<DocumentNumberById documentId={calculation.id} />}
      />
      <DocumentSheetActionsContent>
        <NavigateToDocumentButton
          documentId={calculation.id}
          documentKind={calculation.documentKind}
          processId={calculation.processId}
          serviceKind={GSMServiceKind.RLT}
        />
      </DocumentSheetActionsContent>
    </DocumentSheetActions>
  );
}
